import { computed, inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { doc, docData, DocumentReference, Firestore } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { FirestoreDataService } from 'src/app/services/firestore-data.service';
import { ChallengeAnimationQueueService } from './challenge-animation-queue.service';
import { FlyoutRendererService } from './flyout-renderer.service';
import { shouldPlayPrizeUnlockerKeyAnimation } from './game-page-prize-animation.helper';

/* ------------ Firestore model ------------ */
export interface GameHudDoc {
  /** Prize / balance HUD flyouts (trio, unlocker queue). */
  animate: boolean;
  /** Challenge HUD–related animation pending or playing (separate from `animate`). */
  animate_challenge?: boolean;
  seeded: boolean;
  presentation: string[];
  prize_unlocker?: Data;
  animations: {
    [key: string]: {
      animated: boolean;
      previous: number;
      updated_timestamp?: string;
    };
  };
}

@Injectable({ providedIn: 'root' })
export class GameHudService {
  private firestore = inject(Firestore);
  private flyoutRenderer = inject(FlyoutRendererService);
  private firestoreDataService = inject(FirestoreDataService);
  private injector = inject(Injector);
  private gameHudRef: DocumentReference | null = null;
  private currentBalanceRef: DocumentReference | null = null;
  private postCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private gameNewReturnTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private postCloseInFlight = false;

  // stable writable signals (do NOT reassign these)
  public gameHudData: WritableSignal<GameHudDoc | null> = signal<GameHudDoc | null>(null);
  public currentBalance: WritableSignal<Record<string, number> | null> = signal<Record<string, number> | null>(null);

  /**
   * Bumped when a game session ends so ChallengeSummaryPanel reloads
   * Global_lookup/animations_new (fly / glow / cooldown assets) before post-game sequences.
   */
  readonly challengeAnimationAssetsReloadToken = signal(0);

  // keep subscriptions so we can unsubscribe when UID changes
  private uidSub: Subscription | null = null;
  private gameHudDocSub: Subscription | null = null;
  private currentBalanceDocSub: Subscription | null = null;

  constructor(private authService: AuthService) {
    // subscribe to UID changes and wire up docData subscriptions
    this.uidSub = this.authService.getUid$('SyndicateRepositoryService').subscribe({
      next: (userId) => this.handleUidChange(userId),
      error: (err) => {
        console.warn('Error receiving UID for GameHudService', err);
        // reset signals to null for safety
        this.gameHudData.set(null);
        this.currentBalance.set(null);
      }
    });
  }

  private handleUidChange(userId: string | null | undefined) {
    // clean up any existing doc subscriptions
    this.gameHudDocSub?.unsubscribe();
    this.currentBalanceDocSub?.unsubscribe();
    this.gameHudDocSub = null;
    this.currentBalanceDocSub = null;

    if (!userId) {
      this.gameHudRef = null;
      this.currentBalanceRef = null;
      this.gameHudData.set(null);
      this.currentBalance.set(null);
      return;
    }

    // set refs
    this.gameHudRef = doc(this.firestore, `users/${userId}/balance/game_hud`);
    this.currentBalanceRef = doc(this.firestore, `users/${userId}/balance/current`);

    // subscribe to doc data and write into stable signals
    const gameHud$ = docData(this.gameHudRef) as Observable<GameHudDoc>;
    const balance$ = docData(this.currentBalanceRef) as Observable<Record<string, number>>;

    this.gameHudDocSub = gameHud$.subscribe({
      next: (d) => this.gameHudData.set(d ?? null),
      error: (err) => {
        this.authService.errorHandlerLogs('Error reading gameHud docData', err);
        this.gameHudData.set(null);
      }
    });

    this.currentBalanceDocSub = balance$.subscribe({
      next: (b) => this.currentBalance.set(b ?? null),
      error: (err) => {
        this.authService.errorHandlerLogs('Error reading currentBalance docData', err);
        this.currentBalance.set(null);
      }
    });
  }

  /* ------------ derived ------------ */
   readonly challengeHudAnimateFlag = computed(() => this.gameHudData()?.animate_challenge ?? false);
  readonly animateFlag = computed(() => this.gameHudData()?.animate ?? false);
  readonly presentationQueue = computed(() => this.gameHudData()?.presentation ?? []);
  readonly animationState = computed(() => this.gameHudData()?.animations ?? {});
  readonly prizeUnlockState = computed(() => this.gameHudData()?.animations?.prize_unlocker ?? {});

  /* ========================================================================
     Post-close orchestration (prize queue → challenge queue)
     ====================================================================== */

  /**
   * Schedule post-iframe-close HUD animations after returning to game-new.
   * Owned by this root service so MatDialog/GamePlay destroy cannot cancel it.
   */
  scheduleAfterGameNewReturn(run: () => void, delayMs = 7000): void {
    // Refresh challenge Lottie URLs as soon as the session ends (panel listens on game-new).
    this.requestChallengeAnimationAssetsReload();
    if (this.gameNewReturnTimeoutId !== undefined) {
      clearTimeout(this.gameNewReturnTimeoutId);
    }
    this.gameNewReturnTimeoutId = setTimeout(() => {
      this.gameNewReturnTimeoutId = undefined;
      // Reload again right before prize/challenge queues so assets are fresh.
      this.requestChallengeAnimationAssetsReload();
      try {
        run();
      } catch (err) {
        this.authService.errorHandlerLogs('Error in scheduleAfterGameNewReturn', err);
      }
    }, delayMs);
  }

  /** Ask Challenges HUD to reload Global_lookup animation asset URLs. */
  requestChallengeAnimationAssetsReload(): void {
    this.challengeAnimationAssetsReloadToken.update((n) => n + 1);
  }

  /** Run prize flyouts first, then challenge sequences (no overlap). */
  async runPostCloseSideEffects(): Promise<void> {
    if (this.postCloseInFlight) {
      return;
    }
    this.postCloseInFlight = true;
    try {
      if (this.animateFlag()) {
        await this.startAnimationQueue();
      }
      // Lazy get avoids circular DI with ChallengeAnimationQueueService.
      const challengeQueue = this.injector.get(ChallengeAnimationQueueService);
      await challengeQueue.runIfNeeded();
    } catch (err) {
      this.authService.errorHandlerLogs('Error in runPostCloseSideEffects', err);
    } finally {
      this.postCloseInFlight = false;
    }
  }

  /* ========================================================================
     Queue runner
     ====================================================================== */
  async startAnimationQueue(): Promise<void> {
    const queue = [...this.presentationQueue()];     // FIFO clone
    const lastAward = this.readPrizeUnlockerLastAward();
    const playUnlockerKey = shouldPlayPrizeUnlockerKeyAnimation(queue, lastAward);

    for (const type of queue) {
      try {
        // Demo fix: key / prize_unlocker only when cash or credit was won.
        if (type === 'prize_unlocker' && !playUnlockerKey) {
          await this.removeFromPresentation(type);
          continue;
        }

        // 1. Global asset fetch
        const asset = await this.getAnimationAsset(type);
        if (!asset) {
          await this.removeFromPresentation(type);
          continue;
        }

        // 2. Render paused fly‑out
        await this.renderFlyout(type, asset);

        // 4. 200 ms → fade + play
        await this.delay(200);

        // 6. Remove from presentation array
        await this.removeFromPresentation(type);

        // 7. Wait animation finish (~1 s)
        await this.delay(1000);
      } catch (err) {
        this.authService.errorHandlerLogs(`Error processing ${type}`, err);
      }
    }

    // 8. Reset animate=false when queue done
    await this.resetAnimateFlag();

    if (queue.includes('prize_unlocker')) {
      try {
        const { GamesOnboardingService } = await import(
          '../../services/GamesOnboardingService'
        );
        await this.injector.get(GamesOnboardingService).run();
      } catch (err) {
        // Onboarding evaluation is best-effort after unlocker animation.
        this.authService.errorHandlerLogs(
          'GamesOnboardingService.run after prize_unlocker failed',
          err
        );
      }
    }
  }

  /** `balance/current.prize_unlocker.last_award` — companions may omit presentation keys. */
  private readPrizeUnlockerLastAward(): Record<string, unknown> | null {
    const unlocker = this.currentBalance()?.['prize_unlocker'] as
      | { last_award?: Record<string, unknown> }
      | undefined;
    const last = unlocker?.last_award;
    return last && typeof last === 'object' ? last : null;
  }

  /* ========================================================================
     Helpers
     ====================================================================== */

  private async getAnimationAsset(
    type: string
  ): Promise<{ lottie_url: string } | null> {
    const map: Record<string, string> = {
      gems: 'gems_animation.json',
      points: 'points_animation.json',
      credit: 'credit-animation.json',
      cash: 'cash-animation.json',
      rapid_fire_token: 'rapid_fire_animation.json',
      star_token: 'star_token_animation.json',
      winning_balance: 'cash-animation.json',
      prize_unlocker: 'prize_unlocker_animation.json'
    };

    const filename = map[type];
    return filename ? { lottie_url: filename } : null;
  }

  /** Stub: replace with actual fly‑out renderer/component */
  private async renderFlyout(
    type: string,
    asset: { icon_url?: string; lottie_url?: string }
  ): Promise<void> {
    const delta = this.getDeltaValue(type);
    this.flyoutRenderer.show(type, asset?.lottie_url, delta);
  }

  /** current – previous */
  private getDeltaValue(type: string): number {
    const previous = this.animationState()?.[type]?.previous ?? 0;
    const current = this.currentBalance()?.[type] ?? previous;
    return current - previous;
  }

  /** Pop processed item & clear its animated flag */
  private async removeFromPresentation(type: string): Promise<void> {
    if (!this.gameHudRef) return;
    const path_id = "NC2ObspB6MJ1dS7VLJPH";
    const updateData = {
      presentation: type,
      [`animations.${type}.animated`]: false,
    };

    try {
      await firstValueFrom(
        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data: updateData,
          method: "patch",
        })
      );
    } catch (err) {
      this.authService.errorHandlerLogs('Error updating game HUD animation flag:', err);
    }
  }

  /** Set animate=false so HUD returns to idle */
  private async resetAnimateFlag(): Promise<void> {
    if (!this.gameHudRef) return;
    const path_id = "NC2ObspB6MJ1dS7VLJPH";

    try {
      await firstValueFrom(
        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data: { animate: false },
          method: "update",
        })
      );
    } catch (err) {
      this.authService.errorHandlerLogs('Error updating game HUD animation state:', err);
    }
  }

  /** Promise‑based timeout helper */
  private delay(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

}
